import {
  RatingAndReviewsTypes,
  StockStatuses,
  ButtonVariants
} from '@dcxwam/dcx-wam-ui-foundations'

import { getOptimizedImageExtension } from '../../../../utils/image-extensions'
import { getAssetImageUrl } from '../../../../utils/asset-url'
import { StockLevelStatus } from '../../../../services/stock'
import {
  getStockLevelStatus,
  stockStatusMap,
  getAttributeSelectors,
  getFlavourProfiles,
  getMultibuyPriceForCard,
  getMultibuyDefaultValue,
  getPriceInfo,
  COLOR_PROPERTY
} from '../utils'
import { getStockInfoLabel } from '../../common/stock-info'
import { createDataLayerEcommerceProduct } from '../../common/datalayer/product'
import {
  getOrderQuantityValues,
  isMinQuantityExceedsStock
} from '../../../../utils/quantity-selector'
import {
  checkIsPreorder,
  isValidDate,
  replaceDateFormat
} from '../../../../utils/preorder'
import { formatDateSimple } from '../../../../utils/date-format'
import { setFormSubmitStatus } from '../../../../utils/handle-form-submit'
import { setMarkerProductData } from '../../../../utils/marker-product-data'
import { handleAddToCart } from '../../common/cart-management/handleAddToCart'
import type { HandleAddToCartProps } from '../../common/cart-management/handleAddToCart'
import {
  getRedirectToCartUrl,
  redirectToLoginPage,
  getAddToCartButtonLabel as gatcbl
} from '../../common/cart-management/cartConfig'
import type { CartProductEntry } from '../../common/cart-management/cartConfig'
import { getVariantsWithQty } from '../utils/color-variants'
import { isProductAtMax } from '../utils/auto-delivery-validation'
import { ProductFamilyEnum } from '../models'
import type { ProductDataVariant } from '../../../../services/product/product-data'
import { isFixedProductSet as isFixedProductSetFn } from '../../../../utils/product-set'
import { UserTypes } from '../../../../services/user/user-data'
import {
  BV_FORMS_ID,
  CORESITE_LOCALSTORAGE,
  HYBRIS_ACCESS_TOKEN,
  ORDER_TYPE_ZAS,
  ORDER_TYPE_ZKL,
  REPLACE_PREORDER_DATE
} from '../../../../utils/constants'
import {
  DEFAULT_IMAGE_SIZE,
  EMPTY_IMAGE,
  IMAGE_SIZE,
  MOBILE_IMAGE_SIZE
} from '../utils/constants'
import { getReplacementSerialNumber } from '../utils/replacement-product'
import type { ShopConfig } from '../../common/config'
import type { MuleSoftConfigType } from '../../../../utils/page-config'
import type { FormatPriceOptions } from '../../../../utils/format-price'
import type { Stocks } from '../utils/stock'
import type { UserDataProps } from '../../../../services/user/user-data'
import type { CartLocalStorageJSON } from '../../common/cart-management/types'

export interface VariantDataContext {
  general: {
    linkToPDP?: boolean
    dataLayerListName?: string
    ratingsVisibility?: boolean
    descriptionVisibility?: boolean
    pricesVisibility?: boolean
    showOldPrices?: boolean
    quantitySelectorVisibility?: boolean
    ctaVisibility?: boolean
    ctaAsLinkToPdp?: boolean
    flavourProfilesVisibility?: boolean
    ignoreStock?: boolean
  }
  productRootPath: string
  shopConfig: ShopConfig
  pageLocale: string
  formatPriceOptions?: FormatPriceOptions
  stocks: Stocks
  isSsr: boolean
  attributeSelectorsProp: Array<{
    name: string
    label: string
    showPrice: boolean
  }>
  config: {
    hybrisUrl?: string
    hybrisClientId?: string
    hybrisClientSecret?: string
    baseSiteId?: string
    language?: string
  }
  caConfig: {
    lang?: string
    market?: string
    muleSoftConfig?: MuleSoftConfigType | unknown
  }
  cbxConfig: {
    cbxUrl?: string
    brand?: string
    xChannel?: string
    xCountryCode?: string
    loggerEnabled?: boolean
  }
  multibuySelections: Record<string, number>
  quantitiesSelected: Record<string, number>
  variantCodeByAddToCart: string
  isButtonDisabled: boolean
  isAutoDeliveryEnabled: boolean
  isAutoDeliveryMaximumReached: boolean
  disabledCategoryIds: string[]
  autoDeliveryCartEntries: CartProductEntry[]
  autoDeliveryConfig:
    | {
        addToCartLabel?: string
        removeItemAriaLabel?: string
        decreaseQuantityAriaLabel?: string
        increaseQuantityAriaLabel?: string
      }
    | undefined
  resolvedOrderType: string
  lendingConfigurationCode: string | undefined
  userData: UserDataProps | null | undefined
  cookies: { gig_uid?: string; gig_jwt?: string; hy_at?: string }
  setCookie: (
    name: string,
    value: string,
    options?: {
      path?: string
      maxAge?: number
      secure?: boolean
      sameSite?: 'strict' | 'lax' | 'none'
    }
  ) => void
  cartLocalStorageDataForAddToCart: CartLocalStorageJSON | null | undefined
  setCartLocalStorage: (value: string) => void
  onVariantLink: (
    variant: Partial<ProductDataVariant>,
    url: string,
    meta: { list: string; position: number }
  ) => void
  triggerAutoDeliveryAdd: (
    config: HandleAddToCartProps,
    options?: { productQuantity?: number; enforceMaximum?: boolean }
  ) => void
  setIsButtonDisabled: (disabled: boolean) => void
  setVariantAssetsByAddToCart: (assets: ProductDataVariant['assets']) => void
  setVariantCodeByAddToCart: (code: string) => void
  setErrorMessageContent: (content: {
    message: null
    title: string | undefined
    actions: null
    dismissible: boolean
  }) => void
  setQuantitiesSelected: (
    fn: (prev: Record<string, number>) => Record<string, number>
  ) => void
  setDialogNotifyMeIsOpen: (open: boolean) => void
  setDialogErrorMessageIsOpen: (open: boolean) => void
  setActiveMiniCart: (active: boolean) => void
}

export interface BuildVariantDataParams {
  variant: ProductDataVariant
  variantIndex: number
  variantList: ProductDataVariant[]
  position: number
}

type VariantWithColor = ProductDataVariant & { [key: string]: unknown }

// --- Helper: Compute CTA label ---

const computeAddToCartButtonLabel = (
  variant: ProductDataVariant,
  ctx: VariantDataContext,
  isProductSet: boolean
) => {
  const adAddToCartLabel =
    ctx.isAutoDeliveryEnabled && ctx.autoDeliveryConfig?.addToCartLabel
      ? ctx.autoDeliveryConfig?.addToCartLabel
      : ctx.shopConfig?.addToCartLabel
  const stockLevelStatus = getStockLevelStatus(variant, ctx.stocks)

  const preOrderDate =
    variant?.preOrderUntilDate && isValidDate(variant?.preOrderUntilDate)
      ? replaceDateFormat(variant?.preOrderUntilDate)
      : undefined

  return gatcbl({
    guestAddToCartEnabled: ctx.shopConfig?.guestAddToCartEnabled,
    preOrderUntilDate: preOrderDate,
    stockLevelStatus,
    addToCartLabel: adAddToCartLabel,
    preOrderLabel: ctx.shopConfig?.preOrderCTALabel,
    notifyMe: ctx.shopConfig?.notifyMe,
    notifyMeCTALabel: ctx.shopConfig?.notifyMeCTALabel,
    outOfStockLabel: ctx.shopConfig?.outOfStockCTALabel,
    loginToBuyLabel: ctx.shopConfig?.loginToBuyLabel,
    isProductSet,
    userData: ctx.userData
  })
}

// --- Helper: Build image data ---

interface BuildImageDataParams {
  variantWithColor: VariantWithColor
  variantUrl: string | undefined
  variantIndex: number
  position: number
  ctx: VariantDataContext
}

const buildImageData = (params: BuildImageDataParams) => {
  const { variantWithColor, variantUrl, variantIndex, position, ctx } = params
  const [firstAsset] = variantWithColor.assets || []
  let productImageData = {}
  if (firstAsset) {
    const src = getOptimizedImageExtension(getAssetImageUrl(firstAsset) ?? '')
    productImageData = {
      src: `${src}?width=${DEFAULT_IMAGE_SIZE}`,
      renditions: true,
      srcSetData: [
        `${src}?width=${MOBILE_IMAGE_SIZE} 390w`,
        `${src}?width=${IMAGE_SIZE} 600w`,
        `${src}?width=${IMAGE_SIZE}`
      ].toString()
    }
  }

  return {
    fallback: EMPTY_IMAGE,
    alt: variantWithColor?.globalArticleName,
    ...productImageData,
    width: DEFAULT_IMAGE_SIZE,
    height: DEFAULT_IMAGE_SIZE,
    aspectRatio: true,
    lazyLoading: position !== 1 || variantIndex !== 0,
    ...(variantUrl && {
      linkUrl: variantUrl,
      onClick() {
        ctx.onVariantLink(variantWithColor, variantUrl, {
          list: ctx.general?.dataLayerListName ?? '',
          position
        })
      }
    })
  }
}

// --- Helper: Build product chips ---

const buildProductChips = (variantWithColor: VariantWithColor) => {
  if (!variantWithColor.badges?.length) {
    return undefined
  }
  return {
    chipPropsList: variantWithColor.badges
      .filter(({ badgeDescription }) => Boolean(badgeDescription))
      .map((badge) => ({
        background: badge.backgroundColor?.hexCode,
        color: badge.fontColor?.hexCode,
        label: badge.badgeDescription
      }))
  }
}

// --- Helper: Build name data ---

interface BuildNameDataParams {
  variantWithColor: VariantWithColor
  variantUrl: string | undefined
  position: number
  ctx: VariantDataContext
}

const buildNameData = (params: BuildNameDataParams) => {
  const { variantWithColor, variantUrl, position, ctx } = params
  if (!variantWithColor?.globalArticleName) {
    return undefined
  }
  return {
    productName: variantWithColor.globalArticleName,
    ...(variantUrl && {
      linkUrl: variantUrl,
      onClick() {
        ctx.onVariantLink(variantWithColor, variantUrl, {
          list: ctx.general?.dataLayerListName ?? '',
          position
        })
      }
    })
  }
}

// --- Helper: Build rating ---

interface BuildRatingParams {
  variantWithColor: VariantWithColor
  variantUrl: string | undefined
  ctx: VariantDataContext
}

const buildRating = (params: BuildRatingParams) => {
  const { variantWithColor, variantUrl, ctx } = params
  const showRating =
    ctx.general?.ratingsVisibility && ctx.shopConfig?.ratesAndReviewsEnabled
  if (!showRating) {
    return undefined
  }
  return {
    productId: variantWithColor.globalArticleCode,
    type: RatingAndReviewsTypes.InlineRating,
    reviewsUrl: variantUrl ? `${variantUrl}#${BV_FORMS_ID}` : undefined
  }
}

// --- Helper: Resolve stock status for fixed product sets ---

const resolveFixedProductSetStock = (
  variantWithColor: VariantWithColor,
  stocks: Stocks
) => {
  const refs = variantWithColor.productSetReferences ?? []

  const isOutOfStock = refs.some((ref) =>
    (ref.globalArticleCode ?? []).some((code) => {
      const stock = stocks[code]
      return (
        !stock ||
        stock.stockLevelStatus === StockLevelStatus.outOfStock ||
        (stock.stockLevel != null && stock.stockLevel <= 0)
      )
    })
  )

  if (isOutOfStock) {
    return { isOutOfStock: true, isLowStock: false }
  }

  const isLowStock = refs.some((ref) =>
    (ref.globalArticleCode ?? []).some((code) => {
      const stock = stocks[code]
      return stock?.stockLevelStatus === StockLevelStatus.lowStock
    })
  )

  return { isOutOfStock: false, isLowStock }
}

// --- Helper: Resolve the stock status key ---

interface ResolveStatusKeyParams {
  isFixedProductSet: boolean
  isFixedProductSetOutOfStock: boolean
  isFixedProductSetLowStock: boolean
  effectiveStatusKey: string | undefined
}

const resolveStatusKey = (params: ResolveStatusKeyParams): string => {
  const {
    isFixedProductSet,
    isFixedProductSetOutOfStock,
    isFixedProductSetLowStock,
    effectiveStatusKey
  } = params
  if (!isFixedProductSet) {
    return effectiveStatusKey ?? ''
  }
  if (isFixedProductSetOutOfStock) {
    return StockLevelStatus.outOfStock
  }
  if (isFixedProductSetLowStock) {
    return StockLevelStatus.lowStock
  }
  return StockLevelStatus.inStock
}

// --- Helper: Build stock info ---

const buildStockInfo = (
  resolvedStatusKey: string,
  showStockInfo: boolean,
  ctx: VariantDataContext
) => {
  if (!showStockInfo || !resolvedStatusKey) {
    return undefined
  }
  return {
    stockLabel: getStockInfoLabel({
      isSsr: ctx.isSsr,
      stockLevelStatus: resolvedStatusKey,
      shopConfig: ctx.shopConfig
    }),
    stockStatus: stockStatusMap[resolvedStatusKey],
    showStockMessage:
      stockStatusMap[resolvedStatusKey] !== StockStatuses.InStock ||
      ctx.shopConfig?.showInStockInfoEnable
  }
}

// --- Helper: Build price info ---

const buildPriceInfo = (
  variantWithColor: VariantWithColor,
  ctx: VariantDataContext,
  variantStockLevel: number | undefined
) => {
  if (!ctx.general?.pricesVisibility) {
    return undefined
  }

  const priceInfoConfig = getPriceInfo({
    data: variantWithColor?.price,
    pageLocale: ctx.pageLocale,
    showOldPrices: ctx.isAutoDeliveryEnabled
      ? false
      : ctx.general?.showOldPrices,
    formatPriceOptions: ctx.formatPriceOptions,
    allowSubscriptionPrice: ctx.isAutoDeliveryEnabled
  })

  const { currentPrice, originalPrice, ...requiredConfig } =
    priceInfoConfig ?? {}

  const variantArticleCode = variantWithColor.globalArticleCode ?? ''
  const variantMultiBuyPrice = variantWithColor?.price?.multiBuyPrice
  const hasVariantMultibuy = Boolean(
    ctx.shopConfig?.multibuyEnabled && variantMultiBuyPrice?.length
  )

  const multibuyPriceOverride = getMultibuyPriceForCard({
    hasMultibuy: hasVariantMultibuy,
    multiBuyPrice: variantMultiBuyPrice,
    selectedQty: ctx.multibuySelections[variantArticleCode],
    bestValueToggle: ctx.shopConfig?.multibuyBestValueToggle,
    priceCurrency: variantWithColor?.price?.priceCurrency,
    pageLocale: ctx.pageLocale,
    stockLevel: variantStockLevel,
    formatPriceOptions: ctx.formatPriceOptions
  })

  if (multibuyPriceOverride) {
    return {
      ...requiredConfig,
      currentPriceTextProps: { content: multibuyPriceOverride },
      originalPriceTextProps: undefined
    }
  }

  return requiredConfig
}

// --- Helper: Build add-to-cart method config ---

interface BuildMethodConfigParams {
  variantWithColor: VariantWithColor
  isFixedProductSet: boolean
  productQuantity: number
  ctx: VariantDataContext
}

const buildAddToCartMethodConfig = (
  params: BuildMethodConfigParams
): HandleAddToCartProps => {
  const { variantWithColor, isFixedProductSet, productQuantity, ctx } = params
  return {
    orderType: ctx.resolvedOrderType,
    lendingConfigurationCode: ctx.lendingConfigurationCode,
    redirectToCartOnSuccess: getRedirectToCartUrl(
      ctx.resolvedOrderType,
      ctx.shopConfig?.cartCTAUrl
    ),
    setIsButtonDisabled: ctx.setIsButtonDisabled,
    setIsButtonLoading(bool: boolean) {
      ctx.setVariantAssetsByAddToCart(variantWithColor?.assets ?? [])
      ctx.setVariantCodeByAddToCart(
        bool ? (variantWithColor.globalArticleCode ?? '') : ''
      )
    },
    setErrorMessageContent: (content: {
      message?: string | null
      title?: string | null
      actions?: Array<{
        label?: string
        linkUrl?: string
        onClick?: () => void | Promise<void>
        buttonVariant?: ButtonVariants
      }> | null
      dismissible?: boolean
    }) => {
      ctx.setErrorMessageContent({
        message: content.message ?? null,
        title:
          (content.title ?? ctx.shopConfig?.addToCartErrorMessage) || undefined,
        actions: content.actions ?? null,
        dismissible: content.dismissible ?? true
      } as {
        message: null
        title: string | undefined
        actions: null
        dismissible: boolean
      })
    },
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    isLoginUser: ctx.userData?.decoratedUserType !== UserTypes.Las,
    errorContent: {
      exceededMaximumQuantityTitle:
        ctx.shopConfig?.exceededMaximumQuantityTitle,
      exceededMaximumQuantityMessage:
        ctx.shopConfig?.exceededMaximumQuantityMessage,
      differentOrderTypeTitle: ctx.shopConfig?.differentOrderTypeTitle,
      differentOrderTypeMessage: ctx.shopConfig?.differentOrderTypeMessage,
      differentLoyaltyRequiredTitle:
        ctx.shopConfig?.differentLoyaltyRequiredTitle,
      differentLoyaltyRequiredMessage:
        ctx.shopConfig?.differentLoyaltyRequiredMessage,
      differentOrderTypeConfirmCTALabel:
        ctx.shopConfig?.differentOrderTypeConfirmCTALabel,
      loginRequiredTitle: ctx.shopConfig?.loginRequiredTitle,
      loginRequiredMessage: ctx.shopConfig?.loginRequiredMessage,
      loginRequiredCTALabel: ctx.shopConfig?.loginRequiredCTALabel,
      loginPageURL: ctx.shopConfig?.loginPageURL,
      addToCartDialogCloseAriaLabel:
        ctx.shopConfig?.addToCartDialogCloseAriaLabel,
      addToCartDialogCancelCTALabel:
        ctx.shopConfig?.addToCartDialogCancelCTALabel
    },
    productID: variantWithColor?.globalArticleCode ?? '',
    ...(isFixedProductSet
      ? {
          // Expand each group by its quantity: a group with quantity N
          // contributes N separate entries carrying only the product code
          // (e.g. quantity 2 → the code appears twice). The backend
          // product-set endpoint derives the bundle from repeated codes
          // rather than a per-entry quantity field.
          productEntries: (variantWithColor.productSetReferences ?? []).flatMap(
            (ref) =>
              (ref.globalArticleCode ?? []).flatMap((code) =>
                Array.from({ length: ref.quantity ?? 1 }, () => code)
              )
          )
        }
      : {}),
    currentProductVariant: variantWithColor,
    productQuantity: isFixedProductSet ? 1 : productQuantity,
    hybrisConfig: {
      CA_HYBRIS_URL: ctx.config?.hybrisUrl,
      CA_CLIENT_ID: ctx.config?.hybrisClientId,
      CA_CLIENT_SECRET: ctx.config?.hybrisClientSecret,
      CA_HYBRIS_BASE_SITE_ID: ctx.config?.baseSiteId,
      CA_HYBRIS_LANGUAGE: ctx.config?.language,
      HYBRIS_ACCESS_TOKEN
    },
    defaultErrorMessage: ctx.shopConfig?.addToCartErrorMessage,
    mulesoftConfig:
      ctx.caConfig?.muleSoftConfig &&
      typeof ctx.caConfig?.muleSoftConfig === 'object'
        ? (ctx.caConfig?.muleSoftConfig as MuleSoftConfigType)
        : undefined,
    market: ctx.caConfig?.market,
    dataLayerEcommerceProduct:
      createDataLayerEcommerceProduct(variantWithColor),
    setCookie: ctx.setCookie,
    cookies: ctx.cookies,
    CORESITE_LOCALSTORAGE,
    language: ctx.caConfig?.lang || '',
    setDialogErrorMessageIsOpen: ctx.setDialogErrorMessageIsOpen,
    cartLocalStorageJSON: ctx.cartLocalStorageDataForAddToCart,
    setCartLocalStorage: ctx.setCartLocalStorage,
    setActiveMiniCart: ctx.setActiveMiniCart,
    useCbxOrders:
      ctx.isAutoDeliveryEnabled ||
      ctx.resolvedOrderType === ORDER_TYPE_ZAS ||
      ctx.resolvedOrderType === ORDER_TYPE_ZKL,
    cbxOrdersConfig: {
      baseURL: ctx.cbxConfig?.cbxUrl,
      brand: ctx.cbxConfig?.brand,
      channel: ctx.cbxConfig?.xChannel,
      countryCode: ctx.cbxConfig?.xCountryCode,
      targetGroup: 'b2c',
      loggerEnabled: ctx.cbxConfig?.loggerEnabled
    },
    ...(ctx.resolvedOrderType === ORDER_TYPE_ZKL
      ? {
          replacementType: 'DEVICE' as const,
          replacedSerialNumber: getReplacementSerialNumber() || undefined
        }
      : {})
  }
}

// --- Helper: Build auto-delivery quantity control props ---

interface BuildQuantityControlParams {
  addToCartMethodConfig: HandleAddToCartProps
  defaultAutoDeliveryQuantity: number
  cartEntryQuantity: number
  variantWithColor: VariantWithColor
  ctx: VariantDataContext
}

const buildQuantityControlProps = (params: BuildQuantityControlParams) => {
  const {
    addToCartMethodConfig,
    defaultAutoDeliveryQuantity,
    cartEntryQuantity,
    variantWithColor,
    ctx
  } = params
  return {
    quantity: defaultAutoDeliveryQuantity,
    quantityAriaLabel: ctx.shopConfig?.quantityLabel,
    removeButtonProps: {
      icon: 'pmi-icon-trash',
      'aria-label': ctx.autoDeliveryConfig?.removeItemAriaLabel,
      disabled: defaultAutoDeliveryQuantity <= 0,
      onClick: () => {
        ctx.triggerAutoDeliveryAdd(addToCartMethodConfig, {
          productQuantity: 0,
          enforceMaximum: false
        })
      }
    },
    decreaseButtonProps: {
      icon: 'pmi-icon-minus',
      'aria-label': ctx.autoDeliveryConfig?.decreaseQuantityAriaLabel,
      disabled: defaultAutoDeliveryQuantity <= 1,
      onClick: () => {
        ctx.triggerAutoDeliveryAdd(addToCartMethodConfig, {
          productQuantity: defaultAutoDeliveryQuantity - 1,
          enforceMaximum: false
        })
      }
    },
    increaseButtonProps: {
      icon: 'pmi-icon-plus',
      'aria-label': ctx.autoDeliveryConfig?.increaseQuantityAriaLabel,
      loading:
        ctx.variantCodeByAddToCart ===
        (variantWithColor.globalArticleCode ?? ''),
      disabled:
        ctx.isAutoDeliveryMaximumReached ||
        ctx.disabledCategoryIds.includes(
          variantWithColor.productCategoryCode ??
            variantWithColor.categoryIdentifier ??
            ''
        ) ||
        isProductAtMax(
          cartEntryQuantity,
          variantWithColor.maximumOrderQuantity
        ) ||
        ctx.isButtonDisabled,
      onClick: () => {
        ctx.triggerAutoDeliveryAdd(addToCartMethodConfig, {
          productQuantity: defaultAutoDeliveryQuantity + 1
        })
      }
    }
  }
}

// --- Helper: Build add-to-cart button onClick handler ---

interface BuildOnClickParams {
  variantWithColor: VariantWithColor
  isFixedProductSet: boolean
  addToCartButtonLabel: string | undefined
  addToCartMethodConfig: HandleAddToCartProps
  ctx: VariantDataContext
}

const buildAddToCartOnClick = (params: BuildOnClickParams) => {
  const {
    variantWithColor,
    isFixedProductSet,
    addToCartButtonLabel,
    addToCartMethodConfig,
    ctx
  } = params
  return () => {
    setMarkerProductData(variantWithColor)
    if (
      addToCartButtonLabel === ctx.shopConfig?.loginToBuyLabel &&
      redirectToLoginPage(ctx.shopConfig?.loginPageURL)
    ) {
      return
    }
    if (isFixedProductSet) {
      handleAddToCart(addToCartMethodConfig)
      return
    }

    if (addToCartButtonLabel === ctx.shopConfig?.notifyMeCTALabel) {
      setFormSubmitStatus('')
      ctx.setDialogNotifyMeIsOpen(true)
    }

    const isAddToCartAction = ctx.isAutoDeliveryEnabled
      ? addToCartButtonLabel === ctx.autoDeliveryConfig?.addToCartLabel
      : addToCartButtonLabel === ctx.shopConfig?.addToCartLabel

    if (
      isAddToCartAction ||
      addToCartButtonLabel === ctx.shopConfig?.preOrderCTALabel
    ) {
      if (ctx.isAutoDeliveryEnabled) {
        ctx.triggerAutoDeliveryAdd(addToCartMethodConfig)
      } else {
        handleAddToCart(addToCartMethodConfig)
      }
    }
  }
}

// --- Helper: Compute whether the add-to-cart button is disabled ---

interface ComputeDisabledParams {
  isFixedProductSetOutOfStock: boolean
  addToCartButtonLabel: string | undefined
  variantWithColor: VariantWithColor
  ctx: VariantDataContext
}

const computeAddToCartDisabled = (params: ComputeDisabledParams): boolean => {
  const {
    isFixedProductSetOutOfStock,
    addToCartButtonLabel,
    variantWithColor,
    ctx
  } = params
  return (
    isFixedProductSetOutOfStock ||
    addToCartButtonLabel === ctx.shopConfig?.outOfStockCTALabel ||
    (ctx.isAutoDeliveryEnabled && ctx.isAutoDeliveryMaximumReached) ||
    (ctx.isAutoDeliveryEnabled &&
      ctx.disabledCategoryIds.includes(
        variantWithColor.productCategoryCode ??
          variantWithColor.categoryIdentifier ??
          ''
      )) ||
    ctx.isButtonDisabled
  )
}

// --- Helper: Resolve the add-to-cart button label ---

interface ResolveAddToCartLabelParams {
  isFixedProductSet: boolean
  isFixedProductSetOutOfStock: boolean
  addToCartButtonLabel: string | undefined
  ctx: VariantDataContext
}

const computeResolvedAddToCartLabel = (
  params: ResolveAddToCartLabelParams
): string => {
  const {
    isFixedProductSet,
    isFixedProductSetOutOfStock,
    addToCartButtonLabel,
    ctx
  } = params
  if (!isFixedProductSet) {
    return addToCartButtonLabel ?? ''
  }
  if (isFixedProductSetOutOfStock) {
    return ctx.shopConfig?.outOfStockCTALabel ?? ''
  }
  return ctx.shopConfig?.addToCartLabel ?? ''
}

// --- Helper: Build the full addToCart section ---

interface AddToCartInputs {
  isMixAndMatch: boolean
  isFixedProductSet: boolean
  isFixedProductSetOutOfStock: boolean
  addToCartButtonLabel: string | undefined
  addToCartMethodConfig: HandleAddToCartProps
  isQTYSelectorVisible: boolean | undefined
  orderQTYValues: Array<{ value: string; text: string }> | undefined
  isPreorder: boolean
  preOrderMessage: string | undefined
  defaultAutoDeliveryQuantity: number
  cartEntryQuantity: number
  variantWithColor: VariantWithColor
  variantList: ProductDataVariant[]
  ctx: VariantDataContext
}

const buildAddToCart = (inputs: AddToCartInputs) => {
  const {
    isMixAndMatch,
    isFixedProductSet,
    isFixedProductSetOutOfStock,
    addToCartButtonLabel,
    addToCartMethodConfig,
    isQTYSelectorVisible,
    orderQTYValues,
    isPreorder,
    preOrderMessage,
    defaultAutoDeliveryQuantity,
    cartEntryQuantity,
    variantWithColor,
    variantList,
    ctx
  } = inputs

  if (
    isMixAndMatch ||
    !ctx.general?.ctaVisibility ||
    ctx.general?.ctaAsLinkToPdp
  ) {
    return undefined
  }

  const resolvedLabel = computeResolvedAddToCartLabel({
    isFixedProductSet,
    isFixedProductSetOutOfStock,
    addToCartButtonLabel,
    ctx
  })

  return {
    buttonProps: {
      label: resolvedLabel,
      loading:
        ctx.variantCodeByAddToCart ===
        (variantWithColor.globalArticleCode ?? ''),
      disable: computeAddToCartDisabled({
        isFixedProductSetOutOfStock,
        addToCartButtonLabel,
        variantWithColor,
        ctx
      }),
      onClick: buildAddToCartOnClick({
        variantWithColor,
        isFixedProductSet,
        addToCartButtonLabel,
        addToCartMethodConfig,
        ctx
      })
    },
    qtySelectorProps: {
      'aria-label': ctx.shopConfig?.quantityLabel,
      defaultQTYValue: orderQTYValues?.[0]?.value,
      ...(!isFixedProductSet &&
        isQTYSelectorVisible &&
        addToCartButtonLabel === ctx.shopConfig?.addToCartLabel && {
          orderQTY: orderQTYValues
        })
    },
    ...(!isFixedProductSet &&
      ctx.isAutoDeliveryEnabled && {
        quantityControlProps: buildQuantityControlProps({
          addToCartMethodConfig,
          defaultAutoDeliveryQuantity,
          cartEntryQuantity,
          variantWithColor,
          ctx
        })
      }),
    preOrderMessage: isPreorder ? preOrderMessage : undefined,
    onQTYChange(e: React.ChangeEvent<HTMLInputElement>) {
      ctx.setQuantitiesSelected((prevValue: Record<string, number>) => {
        const target = e.target as HTMLInputElement
        const variantsWithQty = getVariantsWithQty(variantList, target)
        return { ...prevValue, ...variantsWithQty }
      })
    }
  }
}

// --- Helper: Build alternative button props (mix & match / PDP link) ---

interface BuildAlternativeButtonParams {
  isMixAndMatch: boolean
  variantWithColor: VariantWithColor
  variantUrl: string | undefined
  position: number
  ctx: VariantDataContext
}

const buildAlternativeButtonProps = (params: BuildAlternativeButtonParams) => {
  const { isMixAndMatch, variantWithColor, variantUrl, position, ctx } = params

  if (
    !(isMixAndMatch || ctx.general?.ctaAsLinkToPdp) ||
    !ctx.general?.ctaVisibility
  ) {
    return undefined
  }

  const plpToPdpButtonLabel = ctx.shopConfig?.plpToPdpCtaLabel ?? ''
  const mixAndMatchButtonLabel =
    ctx.shopConfig?.mixAndMatchCtaLabel ?? ctx.shopConfig?.addToCartLabel ?? ''

  return {
    label: ctx.general?.ctaAsLinkToPdp
      ? plpToPdpButtonLabel
      : mixAndMatchButtonLabel,
    ...(variantUrl && {
      linkUrl: variantUrl,
      onClick() {
        ctx.onVariantLink(variantWithColor, variantUrl, {
          list: ctx.general?.dataLayerListName ?? '',
          position
        })
      }
    })
  }
}

// --- Helper: Compute product quantity ---

interface ComputeQuantityParams {
  variantWithColor: VariantWithColor
  hasMultibuy: boolean
  variantStockLevel: number | undefined
  ctx: VariantDataContext
}

const computeProductQuantity = (params: ComputeQuantityParams): number => {
  const { variantWithColor, hasMultibuy, variantStockLevel, ctx } = params
  const articleCode = variantWithColor.globalArticleCode ?? ''
  const defaultQuantity = hasMultibuy
    ? Number(
        getMultibuyDefaultValue(
          variantWithColor?.price?.multiBuyPrice,
          ctx.shopConfig?.multibuyBestValueToggle,
          variantStockLevel
        )
      )
    : Number(variantWithColor.minimumOrderQuantity)

  if (ctx.resolvedOrderType === ORDER_TYPE_ZKL) {
    return 1
  }
  return (
    ctx.multibuySelections[articleCode] ??
    ctx.quantitiesSelected[articleCode] ??
    defaultQuantity
  )
}

// --- Helper: Resolve variant URL ---

const resolveVariantUrl = (
  variantWithColor: VariantWithColor,
  ctx: VariantDataContext
): string | undefined => {
  if (!ctx.general?.linkToPDP) {
    return undefined
  }
  return `${ctx.productRootPath?.replace('$SEO_URL_PLACEHOLDER', variantWithColor?.seo?.productsSeoUrl ?? '')}`
}

// --- Helper: Build short description ---

const buildShortDescription = (
  variantWithColor: VariantWithColor,
  ctx: VariantDataContext
) => {
  if (
    !ctx.general?.descriptionVisibility ||
    !variantWithColor.productShortDescription
  ) {
    return undefined
  }
  return { description: variantWithColor.productShortDescription }
}

// --- Helper: Resolve stock context ---

interface StockContext {
  variantStockLevel: number | undefined
  isFixedProductSetOutOfStock: boolean
  isFixedProductSetLowStock: boolean
  statusKey: string | undefined
  effectiveStatusKey: string | undefined
  resolvedStatusKey: string
  showStockInfo: boolean
}

interface ResolveStockContextParams {
  variantWithColor: VariantWithColor
  isFixedProductSet: boolean
  isMixAndMatch: boolean
  ctx: VariantDataContext
}

const resolveStockContext = (
  params: ResolveStockContextParams
): StockContext => {
  const { variantWithColor, isFixedProductSet, isMixAndMatch, ctx } = params
  const variantStockLevel = variantWithColor.globalArticleCode
    ? ctx.stocks[variantWithColor.globalArticleCode]?.stockLevel
    : undefined

  const {
    isOutOfStock: isFixedProductSetOutOfStock,
    isLowStock: isFixedProductSetLowStock
  } = isFixedProductSet
    ? resolveFixedProductSetStock(variantWithColor, ctx.stocks)
    : { isOutOfStock: false, isLowStock: false }

  const statusKey = getStockLevelStatus(variantWithColor, ctx.stocks)
  const effectiveStatusKey = isMinQuantityExceedsStock(
    variantWithColor.minimumOrderQuantity,
    variantStockLevel
  )
    ? StockLevelStatus.outOfStock
    : statusKey

  const resolvedStatusKey = resolveStatusKey({
    isFixedProductSet,
    isFixedProductSetOutOfStock,
    isFixedProductSetLowStock,
    effectiveStatusKey
  })
  const showStockInfo =
    (Boolean(statusKey) || isFixedProductSet) && !isMixAndMatch

  return {
    variantStockLevel,
    isFixedProductSetOutOfStock,
    isFixedProductSetLowStock,
    statusKey,
    effectiveStatusKey,
    resolvedStatusKey,
    showStockInfo
  }
}

// --- Helper: Resolve preorder info ---

interface PreorderInfo {
  orderQTYValues: Array<{ value: string; text: string }>
  preOrderMessage: string | undefined
  isPreorder: boolean
}

const resolvePreorderInfo = (
  variantWithColor: VariantWithColor,
  variantStockLevel: number | undefined,
  ctx: VariantDataContext
): PreorderInfo => {
  const orderQTYValues = getOrderQuantityValues(
    variantWithColor.minimumOrderQuantity,
    variantWithColor.maximumOrderQuantity,
    variantStockLevel
  )
  const locale =
    ctx.caConfig?.lang && ctx.caConfig?.market
      ? `${ctx.caConfig.lang}-${ctx.caConfig.market}`
      : ctx.caConfig?.lang

  const preOrderDate =
    variantWithColor?.preOrderUntilDate &&
    isValidDate(variantWithColor?.preOrderUntilDate)
      ? replaceDateFormat(variantWithColor?.preOrderUntilDate)
      : undefined

  const formattedPreorderDate = formatDateSimple(preOrderDate, locale)
  const preOrderMessage =
    ctx.shopConfig?.preOrderMessage?.replace(
      REPLACE_PREORDER_DATE,
      formattedPreorderDate
    ) || ctx.shopConfig?.preOrderMessage

  const isPreorder = checkIsPreorder(variantWithColor?.preOrderUntilDate)

  return { orderQTYValues, preOrderMessage, isPreorder }
}

// --- Helper: Resolve CTA label and visibility ---

interface CtaContext {
  addToCartButtonLabel: string | undefined
  hasMultibuy: boolean
  isQTYSelectorVisible: boolean | undefined
  cartEntryQuantity: number
  defaultAutoDeliveryQuantity: number
}

interface ResolveCtaContextParams {
  variantWithColor: VariantWithColor
  variantStockLevel: number | undefined
  statusKey: string | undefined
  isPreorder: boolean
  ctx: VariantDataContext
  isProductSet: boolean
}

const resolveCtaContext = (params: ResolveCtaContextParams): CtaContext => {
  const {
    variantWithColor,
    variantStockLevel,
    statusKey,
    isPreorder,
    ctx,
    isProductSet
  } = params
  const minExceedsStock = isMinQuantityExceedsStock(
    variantWithColor.minimumOrderQuantity,
    variantStockLevel
  )
  const minExceedsStockLabel = ctx.shopConfig?.notifyMe
    ? ctx.shopConfig?.notifyMeCTALabel
    : ctx.shopConfig?.outOfStockCTALabel
  const addToCartButtonLabel =
    !isProductSet && !isPreorder && minExceedsStock
      ? minExceedsStockLabel
      : computeAddToCartButtonLabel(variantWithColor, ctx, isProductSet)

  const hasMultibuy = Boolean(
    ctx.shopConfig?.multibuyEnabled &&
    variantWithColor?.price?.multiBuyPrice?.length
  )
  const outOfStock = statusKey
    ? stockStatusMap[statusKey] === StockStatuses.OutOfStock || minExceedsStock
    : minExceedsStock

  const isQTYSelectorVisible =
    ctx.general?.quantitySelectorVisibility &&
    !outOfStock &&
    !isPreorder &&
    !hasMultibuy

  const cartEntryQuantity =
    ctx.autoDeliveryCartEntries.find(
      (entry: CartProductEntry) =>
        entry?.product?.code === variantWithColor.globalArticleCode
    )?.quantity ?? 0
  const defaultAutoDeliveryQuantity = cartEntryQuantity ?? 0

  return {
    addToCartButtonLabel,
    hasMultibuy,
    isQTYSelectorVisible,
    cartEntryQuantity,
    defaultAutoDeliveryQuantity
  }
}

// =============================================================
// Main function — orchestrates helpers, complexity target ≤ 15
// =============================================================

export const buildVariantData = (
  params: BuildVariantDataParams,
  ctx: VariantDataContext
) => {
  const { variant, variantIndex, variantList, position } = params

  const variantWithColor: VariantWithColor = {
    ...variant,
    [COLOR_PROPERTY]: variant.nuance?.code
  }

  const variantUrl = resolveVariantUrl(variantWithColor, ctx)

  const imageData = buildImageData({
    variantWithColor,
    variantUrl,
    variantIndex,
    position,
    ctx
  })
  const productChips = buildProductChips(variantWithColor)
  const nameData = buildNameData({
    variantWithColor,
    variantUrl,
    position,
    ctx
  })
  const rating = buildRating({ variantWithColor, variantUrl, ctx })
  const shortDescriptionData = buildShortDescription(variantWithColor, ctx)

  // --- Product set classification ---
  const isProductSet =
    variantWithColor.productFamily === ProductFamilyEnum.ProductSet
  const isFixedProductSetByReference =
    isProductSet && isFixedProductSetFn(variantWithColor.productSetReferences)
  // Toggle (default true) controls whether a fixed product set is added to the
  // cart directly from the PLP. When disabled, a fixed set behaves like a mix &
  // match set and routes the user to the M&M PDP page instead.
  const allowFixedProductSetAtc =
    ctx.shopConfig?.allowFixedProductSetAtc ?? true
  const isFixedProductSet =
    isFixedProductSetByReference && allowFixedProductSetAtc
  const isMixAndMatch = isProductSet && !isFixedProductSet

  // --- Stock ---
  const stock = resolveStockContext({
    variantWithColor,
    isFixedProductSet,
    isMixAndMatch,
    ctx
  })
  const stockInfo = buildStockInfo(
    stock.resolvedStatusKey,
    stock.showStockInfo,
    ctx
  )

  // --- Price ---
  const priceInfo = buildPriceInfo(
    variantWithColor,
    ctx,
    stock.variantStockLevel
  )
  const editorialDescription = variantWithColor?.editorialDescription

  // --- Attribute selectors ---
  const { attributeSelectors, attributeValues } = isProductSet
    ? { attributeSelectors: undefined, attributeValues: {} }
    : getAttributeSelectors({
        attributeSelectorsProp: ctx.attributeSelectorsProp,
        stocks: ctx.stocks,
        variantList,
        variant: variantWithColor,
        attributeSelectorMarkers:
          ctx.shopConfig?.attributeSelectorMarkers || [],
        locale: ctx.pageLocale,
        formatPriceOptions: ctx.formatPriceOptions
      })

  // --- Preorder & CTA ---
  const preorder = resolvePreorderInfo(
    variantWithColor,
    stock.variantStockLevel,
    ctx
  )
  const cta = resolveCtaContext({
    variantWithColor,
    variantStockLevel: stock.variantStockLevel,
    statusKey: stock.statusKey,
    isPreorder: preorder.isPreorder,
    ctx,
    isProductSet
  })

  const productQuantity = computeProductQuantity({
    variantWithColor,
    hasMultibuy: cta.hasMultibuy,
    variantStockLevel: stock.variantStockLevel,
    ctx
  })

  // --- Add to cart ---
  const addToCartMethodConfig = buildAddToCartMethodConfig({
    variantWithColor,
    isFixedProductSet,
    productQuantity,
    ctx
  })

  const addToCart = buildAddToCart({
    isMixAndMatch,
    isFixedProductSet,
    isFixedProductSetOutOfStock: stock.isFixedProductSetOutOfStock,
    addToCartButtonLabel: cta.addToCartButtonLabel,
    addToCartMethodConfig,
    isQTYSelectorVisible: cta.isQTYSelectorVisible,
    orderQTYValues: preorder.orderQTYValues,
    isPreorder: preorder.isPreorder,
    preOrderMessage: preorder.preOrderMessage,
    defaultAutoDeliveryQuantity: cta.defaultAutoDeliveryQuantity,
    cartEntryQuantity: cta.cartEntryQuantity,
    variantWithColor,
    variantList,
    ctx
  })

  // --- Flavour profiles ---
  const flavourProfiles = isProductSet
    ? undefined
    : getFlavourProfiles(
        variantWithColor,
        ctx.shopConfig,
        ctx.general?.flavourProfilesVisibility ?? false
      )

  // --- Alternative button ---
  const alternativeButtonProps = buildAlternativeButtonProps({
    isMixAndMatch,
    variantWithColor,
    variantUrl,
    position,
    ctx
  })

  return {
    colorValue: variantWithColor.nuance?.code,
    attributeValues,
    imageData,
    productChips,
    nameData,
    rating,
    shortDescriptionData,
    stockInfo,
    priceInfo,
    ...(editorialDescription ? { editorialDescription } : {}),
    ...(addToCart ? { addToCart } : {}),
    ...(attributeSelectors ? { attributeSelectors } : {}),
    ...(flavourProfiles ? { flavourProfiles } : {}),
    code: variantWithColor.globalArticleCode ?? '',
    ...(alternativeButtonProps ? { alternativeButtonProps } : {})
  }
}
